import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isUndefined } from 'lodash-es';
import { LoadingCoordinatorContext } from 'site-modules/personalized-search/context/loading-context/loading-coordinator-context';
import { generateComponentsPaths } from 'site-modules/personalized-search/utils/generate-components-paths';
import { useSearchResults } from 'site-modules/personalized-search/hooks/use-search-results';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

export const checkBeforeCurrentComponentsLoaded = ({ loadedComponentId, components, orderedPaths }) => {
  const indexOfQueuedComponent = orderedPaths.findIndex(id => id === loadedComponentId);
  const componentsBeforeIndexOfQueuedComponent = orderedPaths.slice(0, indexOfQueuedComponent);

  return componentsBeforeIndexOfQueuedComponent.every(path => components[path]);
};

export const LoadingCoordinatorContextProvider = memo(({ children }) => {
  const { pageStruct, isPageStructLoading } = useSearchResults();
  const [loadableComponents, setLoadableComponents] = useState(() => {
    if (pageStruct === null) return { components: {}, orderedPaths: [] };
    if (pageStruct && !isPageStructLoading) {
      const { result: componentsLoadingMap, orderedPaths } = generateComponentsPaths(pageStruct);
      return { components: componentsLoadingMap, orderedPaths };
    }
    return undefined;
  });

  const prevPageStruct = usePrevious(pageStruct);
  const prevIsPageStructLoading = usePrevious(isPageStructLoading);
  if (prevPageStruct !== pageStruct || prevIsPageStructLoading !== isPageStructLoading) {
    if (pageStruct === null) {
      setLoadableComponents({ components: {}, orderedPaths: [] });
    } else if (isPageStructLoading && !isUndefined(loadableComponents)) {
      setLoadableComponents(undefined);
    } else if (pageStruct && !isPageStructLoading) {
      const { result: componentsLoadingMap, orderedPaths } = generateComponentsPaths(pageStruct);
      setLoadableComponents(prev => ({
        components: { ...componentsLoadingMap, ...prev?.components },
        orderedPaths,
      }));
    }
  }

  const setLoadingStatus = useCallback(id => {
    setLoadableComponents(prevValue => {
      const components = prevValue?.components;

      if (!(components && Object.hasOwn(components, id)) || components[id]) {
        return prevValue;
      }

      return { ...prevValue, components: { ...components, [id]: true } };
    });
  }, []);

  const checkPreviousComponentsLoaded = useCallback(
    componentPath => {
      const orderedPaths = loadableComponents?.orderedPaths;
      const components = loadableComponents?.components;

      if (!(orderedPaths && components)) {
        return false;
      }

      return checkBeforeCurrentComponentsLoaded({ loadedComponentId: componentPath, components, orderedPaths });
    },
    [loadableComponents]
  );

  const initialContextValue = useMemo(
    () => ({
      isFullyLoaded:
        !isUndefined(loadableComponents?.components) &&
        (isEmpty(loadableComponents.components) ||
          Object.values(loadableComponents.components).every(componentStatus => !!componentStatus)),
      loadableComponents: loadableComponents?.components,
      checkPreviousComponentsLoaded,
      setLoadingStatus,
      orderedPaths: loadableComponents?.orderedPaths,
    }),
    [checkPreviousComponentsLoaded, loadableComponents, setLoadingStatus]
  );

  return (
    <LoadingCoordinatorContext.Provider value={initialContextValue}>{children}</LoadingCoordinatorContext.Provider>
  );
});

LoadingCoordinatorContextProvider.displayName = 'LoadingCoordinatorContextProviderUI';

LoadingCoordinatorContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
